import type { DependencyList } from 'react';
import { useMemo } from 'react';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';

function checkDeps(deps: DependencyList, name: string) {
  const reactHookName = `React.${name.replace(/DeepCompare/, '')}`;

  if (!deps || deps.length === 0) {
    throw new Error(`${name} should not be used with no dependencies. Use ${reactHookName} instead.`);
  }
}

/**
 * `useDeepCompareMemo` will only recompute the memoized value when one of the
 * `deps` has changed.
 *
 * Usage note: only use this if `deps` are objects or arrays that contain
 * objects. Otherwise you should just use React.useMemo.
 *
 */
export const useDeepCompareMemo = <T>(factory: () => T, dependencies: DependencyList) => {
  if (process.env.NODE_ENV !== 'production') {
    checkDeps(dependencies, 'useDeepCompareMemo');
  }

  // eslint-disable-next-line -- Assuming factory fn is static,  we only care about deps changes
  return useMemo(factory, useDeepCompareMemoize(dependencies));
};
